import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { useAriaLiveRegion } from '../../hooks/use_aria_live_region.js';
import { useHideTimer } from '../../hooks/use_hide_timer.js';
import { getSpacing } from '../../styles/spacing.js';
import { mergeProps } from '../../utils/react.js';
import { Popper } from '../Popper/Popper.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { getMicroToastTransition } from './get_micro_toast_transition.js';
import { jsx } from 'react/jsx-runtime';

var MicroToastContent = styled.div.withConfig({
  displayName: "MicroToast__MicroToastContent",
  componentId: "sc-1qffz6q-0"
})(["&&{background-color:", ";-webkit-box-orient:vertical;-webkit-line-clamp:2;align-items:center;border-radius:", ";color:", ";display:flex;flex-direction:row;font-size:14px;line-height:", ";max-width:", ";min-height:", ";overflow:hidden;padding:", ";text-overflow:ellipsis;white-space:normal;word-break:break-word;}"], function (props) {
  switch (props.$variant) {
    case "success":
      return props.theme.alertColors.success;
    case "error":
      return props.theme.alertColors.error;
    case "warning":
      return props.theme.alertColors.warning;
    case "info":
      return props.theme.alertColors.information;
    case "default":
      return props.theme.neutralColors.smoke;
  }
}, getSpacing(1), function (props) {
  return props.theme.neutralColors.white;
}, getSpacing(7), getSpacing(75), getSpacing(7), function () {
  return "0 ".concat(getSpacing(2));
});
var MicroToast = function MicroToast(props) {
  var _tooltipProps$positio;
  var _useId = useId(1, "micro-toast-"),
    _useId2 = _slicedToArray(_useId, 1),
    defaultId = _useId2[0];
  var children = props.children,
    containerSelector = props.containerSelector,
    content = props.content,
    _props$duration = props.duration,
    duration = _props$duration === void 0 ? 5e3 : _props$duration,
    _props$id = props.id,
    id = _props$id === void 0 ? defaultId : _props$id,
    onHide = props.onHide,
    onShow = props.onShow,
    _props$placement = props.placement,
    placement = _props$placement === void 0 ? "bottom" : _props$placement,
    placementLocked = props.placementLocked,
    showing = props.showing,
    tooltipProps = props.tooltipProps,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? "default" : _props$variant;
  var _useHideTimer = useHideTimer({
      duration: duration,
      showing: showing
    }),
    showingState = _useHideTimer.showingState;
  useAriaLiveRegion({
    content: content,
    showing: showingState !== null && showingState !== void 0 ? showingState : false,
    type: variant === "error" ? "assertive" : "polite"
  });
  if (!content) return tooltipProps ? /*#__PURE__*/jsx(Tooltip, _objectSpread(_objectSpread({
    position: (_tooltipProps$positio = tooltipProps.position) !== null && _tooltipProps$positio !== void 0 ? _tooltipProps$positio : "bottom"
  }, tooltipProps), {}, {
    children: children
  })) : children;
  return /*#__PURE__*/jsx(Popper, {
    containerSelector: containerSelector,
    showing: showingState,
    offset: [0, 11],
    onShow: onShow,
    onHide: onHide,
    transitionProps: getMicroToastTransition,
    placement: placement,
    placementLocked: placementLocked,
    renderContent: function renderContent(_ref) {
      var ref = _ref.ref;
      return /*#__PURE__*/jsx(MicroToastContent, _objectSpread(_objectSpread({}, mergeProps({
        id: id,
        ref: ref,
        role: "micro-toast",
        $variant: variant
      })), {}, {
        children: content
      }));
    },
    renderTrigger: function renderTrigger(_ref2) {
      var ref = _ref2.ref;
      var triggerElement = /*#__PURE__*/React.cloneElement(children, mergeProps(_objectSpread(_objectSpread({}, children.props), {}, {
        ref: children.ref
      }), {
        "aria-describedby": showing ? id : void 0,
        ref: ref
      }));
      if (!showingState && tooltipProps) {
        var _tooltipProps$positio2;
        return /*#__PURE__*/jsx(Tooltip, _objectSpread(_objectSpread({
          position: (_tooltipProps$positio2 = tooltipProps.position) !== null && _tooltipProps$positio2 !== void 0 ? _tooltipProps$positio2 : "bottom"
        }, tooltipProps), {}, {
          children: triggerElement
        }));
      }
      return triggerElement;
    }
  });
};
MicroToast.displayName = "MicroToast";

export { MicroToast };
